import { Link, useNavigate } from "react-router-dom";
import { Coins, Menu, X, User, Settings, CreditCard, HelpCircle, LogOut } from "lucide-react";
import { useState } from "react";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

export const Navbar = () => {
  const { user } = useAuth();
  const { subscription, isLoading } = useSubscription();
  const balance = subscription?.credits_remaining ?? 0;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <>
      {/* Skip Navigation for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to main content
      </a>

      <nav 
        className="sticky top-0 z-50 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md border-b border-border/50"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity group"
            >
              <img 
                src={logo} 
                alt="Email2Contact Logo" 
                className="w-8 h-8 transition-transform group-hover:scale-105" 
              />
              <span className="text-xl font-bold">Email2Contact</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/pricing" 
                className="text-sm font-medium hover:text-primary transition-colors"
              >
                Pricing
              </Link>
              {user && (
                <Link 
                  to="/support" 
                  className="text-sm font-medium hover:text-primary transition-colors"
                >
                  Support
                </Link>
              )}
              
              {user ? (
                <div className="flex items-center gap-3">
                  {/* Credit Badge - clickable */}
                  <Link 
                    to="/settings?tab=credits"
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 rounded-full transition-colors"
                    aria-label={`Current balance: ${balance} credits. Click to buy more.`}
                  >
                    <Coins className="w-4 h-4 text-primary" aria-hidden="true" />
                    {isLoading ? (
                      <Skeleton className="h-4 w-8" />
                    ) : (
                      <span className="text-sm font-medium">{balance}</span>
                    )}
                  </Link>

                  {/* User Menu Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="rounded-full"
                        aria-label="Open user menu"
                      >
                        <User className="w-5 h-5" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/pricing" className="flex items-center gap-2 cursor-pointer">
                          <CreditCard className="w-4 h-4" />
                          Plans & Pricing
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/support" className="flex items-center gap-2 cursor-pointer">
                          <HelpCircle className="w-4 h-4" />
                          Support & FAQ
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={handleSignOut}
                        className="text-destructive focus:text-destructive cursor-pointer"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Button 
                  variant="outline"
                  asChild
                >
                  <Link to="/auth">Sign In</Link>
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay & Slide-out */}
        {isMobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="fixed top-[73px] right-0 w-64 bg-white dark:bg-slate-900 shadow-lg z-50 md:hidden border-l border-border">
              <nav className="flex flex-col p-4 space-y-4" role="menu">
                <Link 
                  to="/pricing" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium hover:text-primary transition-colors py-2"
                  role="menuitem"
                >
                  Pricing
                </Link>
                {user && (
                  <>
                    <Link 
                      to="/support" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-sm font-medium hover:text-primary transition-colors py-2"
                      role="menuitem"
                    >
                      Support
                    </Link>
                    <Link 
                      to="/settings?tab=credits"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 rounded-full transition-colors"
                      role="menuitem"
                    >
                      <Coins className="w-4 h-4 text-primary" />
                      {isLoading ? (
                        <Skeleton className="h-4 w-8" />
                      ) : (
                        <span className="text-sm font-medium">{balance} credits</span>
                      )}
                    </Link>
                    <Link 
                      to="/settings" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-2 text-sm font-medium hover:text-primary transition-colors"
                      role="menuitem"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 py-2 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors text-left"
                      role="menuitem"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </>
                )}
                {!user && (
                  <Button 
                    variant="outline"
                    className="w-full"
                    asChild
                  >
                    <Link to="/auth">Sign In</Link>
                  </Button>
                )}
              </nav>
            </div>
          </>
        )}
      </nav>
    </>
  );
};
